import { useAtomValue, useSetAtom, useStore } from "jotai";
import { type JSX, useEffect, useRef, useState } from "react";

import { useArrayField } from "@frontend/hooks/form";
import type { ArrayItemField, NumberField } from "@frontend/lib/form";
import { ContactField, contactsAtom } from "@frontend/shared/contact-field";
import {
  EmployeeField,
  employeesAtom,
  employeesLoadedAtom,
  loadEmployeesAtom,
} from "@frontend/shared/employee-field";
import { InterlocutorDialog } from "@frontend/shared/interlocutor-dialog";
import type { InterlocutorValues } from "@frontend/shared/interlocutor-dialog";
import {
  PeopleGroup,
  PeopleGroups,
  PersonCard,
} from "@frontend/shared/people-group";
import { useLingui } from "@lingui/react/macro";

import { clientAtom, clientForm, optionsAtom } from "../stores/editor";
import { SectionFrame } from "./Section";

const fields = clientForm.fields;

type ContactItem = ArrayItemField<typeof fields.contacts>;

type AccountantItem = ArrayItemField<typeof fields.accountants>;

type ReferenceAtom = NumberField<number | null | undefined>;

type Editing = {
  group: "contacts" | "accountants";
  index: number | null;
  values: InterlocutorValues;
  fallbackLabel: string | null;
};

const empty: InterlocutorValues = { personId: null, typeId: null };

export function Interlocutors(): JSX.Element {
  const { t } = useLingui();

  const store = useStore();

  const contacts = useArrayField(fields.contacts);
  const accountants = useArrayField(fields.accountants);

  const options = useAtomValue(optionsAtom);
  const employees = useAtomValue(employeesAtom);
  const employeesLoaded = useAtomValue(employeesLoadedAtom);

  const loadEmployees = useSetAtom(loadEmployeesAtom);

  const [editing, setEditing] = useState<Editing | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const originRef = useRef<DOMRect | null>(null);

  useEffect(() => {
    void loadEmployees();
  }, [loadEmployees]);

  // The line survives the closing, which the window animates on: what it holds
  // is only read again the next time it opens.
  const edit = (next: Editing, origin: DOMRect): void => {
    originRef.current = origin;

    setEditing(next);
    setEditorOpen(true);
  };

  const commit = (values: InterlocutorValues): void => {
    if (null === editing) {
      return;
    }

    const { group, index } = editing;

    if ("contacts" === group) {
      if (null === index) {
        contacts.append({
          id: null,
          contactId: values.personId,
          typeId: values.typeId,
        });

        return;
      }

      const item = contacts.items[index];

      write(store, item?.fields.contactId, values.personId);
      write(store, item?.fields.typeId, values.typeId);

      return;
    }

    if (null === index) {
      accountants.append({
        id: null,
        employeeId: values.personId,
        typeId: values.typeId,
      });

      return;
    }

    const item = accountants.items[index];

    write(store, item?.fields.employeeId, values.personId);
    write(store, item?.fields.typeId, values.typeId);
  };

  const adding = null === editing?.index;
  const forContacts = "accountants" !== editing?.group;

  return (
    <SectionFrame icon="Users" legend={t`Interlocutors`}>
      <PeopleGroups>
        <PeopleGroup
          title={t`Contacts`}
          count={contacts.count}
          empty={t`Nobody is named at this client yet.`}
          addLabel={t`Add a contact`}
          onAdd={(origin) =>
            edit(
              {
                group: "contacts",
                index: null,
                values: empty,
                fallbackLabel: null,
              },
              origin,
            )
          }
        >
          {contacts.items.map((contact, index) => (
            <ClientContactCard
              key={contact.path}
              item={contact}
              onEdit={(values, fallbackLabel, origin) =>
                edit(
                  { group: "contacts", index, values, fallbackLabel },
                  origin,
                )
              }
              onRemove={() => contacts.remove(contacts.itemAtoms[index]!)}
            />
          ))}
        </PeopleGroup>

        <PeopleGroup
          title={t`In charge`}
          count={accountants.count}
          empty={t`Nobody follows this client yet.`}
          addLabel={t`Add someone`}
          loading={!employeesLoaded}
          addDisabled={0 === employees.length}
          onAdd={(origin) =>
            edit(
              {
                group: "accountants",
                index: null,
                values: empty,
                fallbackLabel: null,
              },
              origin,
            )
          }
        >
          {accountants.items.map((accountant, index) => (
            <AccountantCard
              key={accountant.path}
              item={accountant}
              onEdit={(values, fallbackLabel, origin) =>
                edit(
                  { group: "accountants", index, values, fallbackLabel },
                  origin,
                )
              }
              onRemove={() => accountants.remove(accountants.itemAtoms[index]!)}
            />
          ))}
        </PeopleGroup>
      </PeopleGroups>

      <InterlocutorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        originRef={originRef}
        icon={adding ? "UserPlus" : "UserPen"}
        title={
          forContacts
            ? adding
              ? t`Add a contact`
              : t`Change the contact`
            : adding
              ? t`Add someone in charge`
              : t`Change who is in charge`
        }
        personLabel={forContacts ? t`Contact` : t`Employee`}
        personControl={
          // Both lists are long enough to be searched rather than offered: the
          // address book on one side, the roster on the other.
          forContacts
            ? (field) => <ContactField field={field} />
            : (field) => (
                <EmployeeField
                  field={field}
                  fallbackLabel={editing?.fallbackLabel}
                />
              )
        }
        parts={forContacts ? options.contactTypes : options.accountantTypes}
        values={editing?.values ?? empty}
        fallbackLabel={editing?.fallbackLabel}
        onSubmit={commit}
      />
    </SectionFrame>
  );
}

/** A line already on the form is written through its own field atoms. */
function write(
  store: ReturnType<typeof useStore>,
  field: ReferenceAtom | undefined,
  value: number | null,
): void {
  if (undefined === field) {
    return;
  }

  store.set(field.atom, value);
  store.set(field.touch);
}

type CardProps<TItem> = {
  item: TItem;
  onEdit: (
    values: InterlocutorValues,
    fallbackLabel: string | null,
    origin: DOMRect,
  ) => void;
  onRemove: () => void;
};

function ClientContactCard({
  item,
  onEdit,
  onRemove,
}: CardProps<ContactItem>): JSX.Element {
  const options = useAtomValue(optionsAtom);
  const client = useAtomValue(clientAtom);
  const people = useAtomValue(contactsAtom);

  const contactId = useAtomValue(item.fields.contactId.atom);
  const typeId = useAtomValue(item.fields.typeId.atom);

  // The record says who a saved line is; a line just named reads its name from
  // the list the picker offered.
  const known = client?.contacts.find(
    (contact) => contact.contactId === contactId,
  );

  const picked = people.find((person) => person.id === contactId);

  const name = known?.contactDisplayName ?? picked?.displayName ?? null;

  return (
    <PersonCard
      name={name}
      hint={null}
      avatarUrl={known?.avatarUrl ?? picked?.avatarUrl ?? null}
      role={
        options.contactTypes.find(({ id }) => id === typeId)?.label ??
        known?.typeLabel ??
        null
      }
      onEdit={(origin) =>
        onEdit(
          { personId: contactId ?? null, typeId: typeId ?? null },
          name,
          origin,
        )
      }
      onRemove={onRemove}
    />
  );
}

function AccountantCard({
  item,
  onEdit,
  onRemove,
}: CardProps<AccountantItem>): JSX.Element {
  const options = useAtomValue(optionsAtom);
  const employees = useAtomValue(employeesAtom);
  const client = useAtomValue(clientAtom);

  const employeeId = useAtomValue(item.fields.employeeId.atom);
  const typeId = useAtomValue(item.fields.typeId.atom);

  const known = client?.accountants.find(
    (accountant) => accountant.employeeId === employeeId,
  );

  const option = employees.find(({ id }) => id === employeeId);

  return (
    <PersonCard
      name={option?.label ?? known?.employeeLabel ?? null}
      hint={option?.department ?? null}
      avatarUrl={option?.avatarUrl ?? null}
      role={
        options.accountantTypes.find(({ id }) => id === typeId)?.label ??
        known?.typeLabel ??
        null
      }
      onEdit={(origin) =>
        onEdit(
          { personId: employeeId ?? null, typeId: typeId ?? null },
          known?.employeeLabel ?? null,
          origin,
        )
      }
      onRemove={onRemove}
    />
  );
}
